import { createId, roundMoney } from '@/engines/budget'

import { commercialCatalog, type CommercialCatalog } from '../catalog'
import type {
  CommercialComponent,
  CommercialItem,
  ComputerBuilderState,
  ComputerSlot,
  ComputerValidationResult,
} from '../types'
import { COMPUTER_REQUIRED_SLOTS } from '../types'

function entryToComponent(
  slot: ComputerSlot,
  catalogId: string,
  catalog: CommercialCatalog,
  quantity = 1,
): CommercialComponent {
  const entry = catalog.getById(catalogId)
  if (!entry) {
    throw Object.assign(new Error(`Componente não encontrado no catálogo: ${catalogId}`), {
      code: 'CATALOG_NOT_FOUND',
    })
  }
  if (entry.type === 'Component' && entry.slot && entry.slot !== slot) {
    throw Object.assign(
      new Error(`Slot inválido: ${catalogId} é ${entry.slot}, esperado ${slot}`),
      { code: 'SLOT_MISMATCH' },
    )
  }
  return {
    slot,
    catalogId: entry.id,
    label: entry.label,
    brand: entry.brand,
    model: entry.model,
    quantity,
    unitCost: entry.unitCost,
    unitPrice: entry.unitPrice,
  }
}

function sumComponents(components: CommercialComponent[]): { unitCost: number; unitPrice: number } {
  let unitCost = 0
  let unitPrice = 0
  for (const c of components) {
    unitCost += c.unitCost * c.quantity
    unitPrice += c.unitPrice * c.quantity
  }
  return {
    unitCost: roundMoney(unitCost),
    unitPrice: roundMoney(unitPrice),
  }
}

function describeBom(components: CommercialComponent[]): string {
  return components
    .map((c) => `${c.slot.toUpperCase()}: ${c.brand} ${c.model}`)
    .join(' · ')
}

/**
 * ComputerBuilder — Computer is a configuration, not a product SKU.
 * Output: CommercialItem type=Computer compatible with Budget Engine mapping.
 */
export class ComputerBuilder {
  private state: ComputerBuilderState = {
    name: 'Computador',
    description: '',
    quantity: 1,
    slots: {},
  }
  private readonly catalog: CommercialCatalog

  constructor(catalog: CommercialCatalog = commercialCatalog) {
    this.catalog = catalog
  }

  reset(name = 'Computador'): this {
    this.state = { name, description: '', quantity: 1, slots: {} }
    return this
  }

  setName(name: string): this {
    this.state.name = name.trim() || 'Computador'
    return this
  }

  setDescription(description: string): this {
    this.state.description = description
    return this
  }

  setQuantity(quantity: number): this {
    this.state.quantity = Math.max(1, Math.floor(quantity))
    return this
  }

  /** Assign a catalog part to a slot. */
  setSlot(slot: ComputerSlot, catalogId: string, quantity = 1): this {
    this.state.slots[slot] = entryToComponent(slot, catalogId, this.catalog, quantity)
    return this
  }

  clearSlot(slot: ComputerSlot): this {
    delete this.state.slots[slot]
    return this
  }

  getState(): ComputerBuilderState {
    return {
      ...this.state,
      slots: { ...this.state.slots },
    }
  }

  validate(): ComputerValidationResult {
    const issues: ComputerValidationResult['issues'] = []
    for (const slot of COMPUTER_REQUIRED_SLOTS) {
      if (!this.state.slots[slot]) {
        issues.push({
          severity: 'error',
          code: 'MISSING_SLOT',
          message: `Slot obrigatório ausente: ${slot}`,
          slot,
        })
      }
    }
    if (!this.state.slots.gpu) {
      issues.push({
        severity: 'warning',
        code: 'NO_GPU',
        message: 'Nenhuma GPU definida — considere vídeo integrado ou dedicada',
        slot: 'gpu',
      })
    }
    return { valid: issues.every((i) => i.severity !== 'error'), issues }
  }

  /**
   * Finalize configuration → CommercialItem (Computer).
   * Throws if required slots are missing.
   */
  build(options?: { originTemplateId?: string }): CommercialItem {
    const validation = this.validate()
    if (!validation.valid) {
      const msg = validation.issues
        .filter((i) => i.severity === 'error')
        .map((i) => i.message)
        .join('; ')
      throw Object.assign(new Error(msg || 'Configuração inválida'), {
        code: 'COMPUTER_INVALID',
        issues: validation.issues,
      })
    }

    const components = COMPUTER_REQUIRED_SLOTS.map((s) => this.state.slots[s]!).concat(
      (['gpu', 'cooler', 'other'] as ComputerSlot[])
        .map((s) => this.state.slots[s])
        .filter((c): c is CommercialComponent => Boolean(c)),
    )

    // Deduplicate by slot (required already unique; optional may overlap order)
    const bySlot = new Map<ComputerSlot, CommercialComponent>()
    for (const c of components) bySlot.set(c.slot, c)
    const ordered = [...bySlot.values()]

    const totals = sumComponents(ordered)
    const bom = describeBom(ordered)

    return {
      id: createId(),
      type: 'Computer',
      name: this.state.name,
      description: this.state.description.trim() || bom,
      quantity: this.state.quantity,
      unitCost: totals.unitCost,
      unitPrice: totals.unitPrice,
      components: ordered,
      origin: {
        source: options?.originTemplateId ? 'template' : 'builder',
        templateId: options?.originTemplateId,
      },
      meta: {
        commercialType: 'Computer',
        componentCount: String(ordered.length),
      },
    }
  }
}

export function createComputerBuilder(catalog?: CommercialCatalog): ComputerBuilder {
  return new ComputerBuilder(catalog)
}
